/**
 * The DOM heads-up display layered over the WebGL stage.
 *
 * Why DOM and not in-world 3D text: crisp bilingual type at any DPI, real focus
 * management, and screen-reader access. The approved reference does the same —
 * "Pixi world rendering sits inside a bordered game frame. DOM HUD and controls
 * must respect the frame." We inherit that split deliberately.
 *
 * Layout mirrors the reference screenshots: HOLDING panel top-left, a centred
 * SCORE / PROGRESS / LIVES / TIME bar, a round pause button top-right, a large
 * joystick bottom-left and the red A / green B buttons bottom-right.
 *
 * Three principles govern everything below.
 *
 *   1. NUMBERS MOVE. A score that snaps from 100 to 220 reads as a data update;
 *      a score that counts up and pulses reads as a reward. Same information,
 *      completely different feel. Every stat here interpolates.
 *
 *   2. NEVER SIGNAL WITH COLOUR ALONE. The urgent clock changes hue *and*
 *      gains a pill background, an exclamation mark and a pulse. Lives are five
 *      discrete pips whose fill, inner core and silhouette all change when
 *      spent, so the state survives colour-vision differences and greyscale.
 *
 *   3. THE FRAME LOOP IS SACRED. `render()` runs on every session emit, i.e.
 *      every frame. Every write here is guarded by an equality check, and the
 *      only JS tween (the score counter) runs on its own rAF that stops itself
 *      the moment it settles.
 */
import { formatTime, MAX_LIVES, type SessionSnapshot } from '../core/session';
import type { Input } from '../core/input';
import { clear, el, prefersReducedMotion, restartAnimation, setText, svg } from './dom';

export interface HudOptions {
  root: HTMLElement;
  input: Input;
  onPause: () => void;
  onExit: () => void;
}

/** Feedback tone for {@link Hud.flash}. Drives icon, accent colour and duration. */
export type FlashKind = 'correct' | 'wrong' | 'info';

/** Which on-screen action button. Matches `Input.pressButton`. */
export type ActionKey = 'a' | 'b';

/**
 * Icon shown inside an action button. `hand` is the reference default for A;
 * the rest exist so Game 2 can retitle the same button as the player's verb
 * changes (pick up -> insert -> scan -> deposit) without inventing new controls.
 *
 * `die` is Game 3's, and it is the reason this union is worth widening rather
 * than reusing `scan`: a board game's one repeated action is the roll, and a
 * magnifier on that button would teach the wrong verb from the first press.
 */
export type GlyphKind =
  | 'hand'
  | 'grab'
  | 'scan'
  | 'drop'
  | 'back'
  | 'menu'
  | 'die'
  | 'a'
  | 'b';

/** Milliseconds the score counter takes to travel to a new value. */
const SCORE_TWEEN_MS = 520;

/** Auto-dismiss timings. Wrong answers linger — they have something to teach. */
const FLASH_MS: Record<FlashKind, number> = { correct: 2200, wrong: 4200, info: 2800 };

/* ------------------------------------------------------------------ *
 * Icon glyphs
 *
 * Every glyph is built from primitives rather than a font or an external SVG
 * file, for the same reason the 3D world is: this project ships no art assets.
 * Rounded rectangles read as chunky ink shapes at button size and stay crisp at
 * any DPI.
 * ------------------------------------------------------------------ */

function svgRoot(...children: Node[]): SVGElement {
  return svg(
    'svg',
    {
      viewBox: '0 0 24 24',
      'aria-hidden': 'true',
      focusable: 'false',
      class: 'hud-glyph',
    },
    ...children
  );
}

function stroke(d: string, width = 3.2): SVGElement {
  return svg('path', {
    d,
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': width,
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
  });
}

function bar(x: number, y: number, w: number, h: number, extra: Record<string, string | number> = {}) {
  return svg('rect', { x, y, width: w, height: h, rx: Math.min(w, h) / 2, fill: 'currentColor', ...extra });
}

function glyph(kind: GlyphKind): Node {
  switch (kind) {
    case 'hand':
      // The approved Game 1 interaction hand. Keep its authored silhouette
      // intact instead of rebuilding it from rounded rectangles: at button
      // size the gentler thumb and rounded palm are noticeably clearer.
      return svg(
        'svg',
        {
          viewBox: '228 121 264 264',
          'aria-hidden': 'true',
          focusable: 'false',
          class: 'hud-glyph',
        },
        svg('path', {
          transform: 'translate(6 0)',
          fill: 'currentColor',
          d: 'M291 299V188c0-15 23-15 23 0v48h14v-70c0-16 25-16 25 0v70h14v-78c0-16 25-16 25 0v78h14v-57c0-15 23-15 23 0v95c0 47-31 85-69 85-32 0-58-7-67-35-5-5-12-10-20-15l-14-21c-8-6-7-18 1-24 8-6 17-2 24 5l12 27Z',
        })
      );
    case 'grab':
      // A closed fist: the same hand, carrying something.
      return svgRoot(
        bar(7.6, 7.0, 2.9, 5.4),
        bar(10.8, 6.2, 2.9, 6.2),
        bar(14.0, 6.6, 2.9, 5.8),
        bar(17.2, 8.0, 2.6, 4.4),
        svg('rect', { x: 6.6, y: 10.6, width: 14.0, height: 10.0, rx: 4.6, fill: 'currentColor' }),
        svg('rect', { x: 5.0, y: 13.0, width: 9.0, height: 3.4, rx: 1.7, fill: 'currentColor' })
      );
    case 'scan':
      // Magnifier over a readout — Game 2's identification machines.
      return svgRoot(
        svg('circle', {
          cx: 10.6,
          cy: 10.6,
          r: 6.3,
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 2.8,
        }),
        bar(7.6, 8.4, 6.0, 1.5),
        bar(7.6, 11.4, 3.8, 1.5),
        stroke('M15.4 15.4 L20.2 20.2', 3.2)
      );
    case 'drop':
      // Arrow falling into a tray — deposit into the matching bin.
      return svgRoot(
        stroke('M12 3.4 V13.4', 3.0),
        stroke('M7.8 10.0 L12 14.4 L16.2 10.0', 3.0),
        svg('rect', { x: 3.8, y: 17.0, width: 16.4, height: 4.2, rx: 1.8, fill: 'currentColor' })
      );
    case 'back':
      return svgRoot(stroke('M14.8 5.2 L8.0 12 L14.8 18.8', 3.4));
    case 'menu':
      return svgRoot(bar(4.6, 6.0, 14.8, 2.9), bar(4.6, 10.6, 14.8, 2.9), bar(4.6, 15.2, 14.8, 2.9));
    case 'die':
      // Game 3's roll button. Five pips rather than three or four: at 24px an
      // odd, corner-heavy arrangement reads as a die, while a diagonal of two
      // or three reads as a typo. Outline stroked, pips filled, so it survives
      // both cream-on-red and red-on-cream button states.
      return svgRoot(
        svg('rect', {
          x: 3.8,
          y: 3.8,
          width: 16.4,
          height: 16.4,
          rx: 4.2,
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 2.6,
        }),
        svg('circle', { cx: 8.3, cy: 8.3, r: 1.65, fill: 'currentColor' }),
        svg('circle', { cx: 15.7, cy: 8.3, r: 1.65, fill: 'currentColor' }),
        svg('circle', { cx: 12.0, cy: 12.0, r: 1.65, fill: 'currentColor' }),
        svg('circle', { cx: 8.3, cy: 15.7, r: 1.65, fill: 'currentColor' }),
        svg('circle', { cx: 15.7, cy: 15.7, r: 1.65, fill: 'currentColor' })
      );
    case 'a':
    case 'b':
      return el('span', { class: 'hud-glyph hud-glyph--letter', text: kind.toUpperCase() });
  }
}

function flashGlyph(kind: FlashKind): Node {
  if (kind === 'correct') return svgRoot(stroke('M5.2 12.6 L9.9 17.4 L18.9 6.6', 3.4));
  if (kind === 'wrong') return svgRoot(stroke('M7.0 7.0 L17.0 17.0', 3.4), stroke('M17.0 7.0 L7.0 17.0', 3.4));
  return svgRoot(stroke('M12 6.6 V13.4', 3.4), stroke('M12 17.4 V17.5', 3.6));
}

/* ------------------------------------------------------------------ *
 * HUD
 * ------------------------------------------------------------------ */

export class Hud {
  private readonly root: HTMLElement;
  private readonly input: Input;

  private wrap!: HTMLElement;
  private topbar!: HTMLElement;
  private scoreEl!: HTMLElement;
  private progressEl!: HTMLElement;
  private progressBar!: HTMLElement;
  private livesEl!: HTMLElement;
  private timeEl!: HTMLElement;
  private timeStat!: HTMLElement;
  private holdingPanel!: HTMLElement;
  private holdingIcon!: HTMLElement;
  private holdingName!: HTMLElement;
  private objectiveEl!: HTMLElement;
  private objectiveTitle!: HTMLElement;
  private objectiveDetail!: HTMLElement;
  private promptEl!: HTMLElement;
  private promptText!: HTMLElement;
  private promptKey!: HTMLElement;
  private flashEl!: HTMLElement;
  private flashIcon!: HTMLElement;
  private flashTitle!: HTMLElement;
  private flashDetail!: HTMLElement;
  private hitFrame!: HTMLElement;
  private stickBase!: HTMLElement;
  private stickKnob!: HTMLElement;
  private controlsEl!: HTMLElement;
  private buttons!: Record<ActionKey, HTMLButtonElement>;
  private glyphSlots: Partial<Record<ActionKey, HTMLElement>> = {};
  /** Last glyph written to each slot, so per-frame callers become a no-op. */
  private glyphKinds: Partial<Record<ActionKey, GlyphKind>> = {};
  private exitBtn!: HTMLButtonElement;
  private live!: HTMLElement;

  private pips: HTMLElement[] = [];

  private disposers: Array<() => void> = [];
  private visible = false;

  /** Cached last-rendered values so the per-frame `render` never writes twice. */
  private shownScore = 0;
  private targetScore = 0;
  private tweenFrom = 0;
  private tweenStart = 0;
  private scoreRaf = 0;
  private shownLives = MAX_LIVES;
  private shownProgress = '';
  private shownPct = -1;
  private shownTime = '';
  private shownPhase = '';
  private warned30 = false;
  private warned10 = false;

  private promptCache = '';
  private flashTimer = 0;
  private announceTimer = 0;
  private stickReturnTimer = 0;

  constructor(private readonly opts: HudOptions) {
    this.root = opts.root;
    this.input = opts.input;
    this.build();
  }

  /* ---------------------------------------------------------------- *
   * Construction
   * ---------------------------------------------------------------- */

  private statShell(label: string, modifier = ''): { node: HTMLElement; body: HTMLElement } {
    const body = el('div', { class: 'hud-stat__body' });
    const node = el(
      'div',
      { class: `hud-stat${modifier}` },
      el('span', { class: 'hud-stat__label', text: label }),
      body
    );
    return { node, body };
  }

  private build(): void {
    clear(this.root);

    this.wrap = el('div', {
      class: 'hud',
      attrs: { 'data-hud': '' },
      style: 'position:absolute;inset:0;pointer-events:none;',
    });

    // --- Top bar: SCORE / PROGRESS / LIVES / TIME -------------------------
    // Deliberately NOT role="status": the clock changes every frame, and a live
    // region here would make a screen reader recite the whole bar 60 times a
    // second. Announcements go through the dedicated polite region instead.
    const score = this.statShell('SCORE');
    this.scoreEl = el('span', { class: 'hud-stat__value', text: '0' });
    score.body.append(this.scoreEl);

    const progress = this.statShell('PROGRESS', ' hud-stat--wide');
    this.progressEl = el('span', { class: 'hud-stat__value', text: '0/0' });
    this.progressBar = el('div', { class: 'hud-progress__fill' });
    progress.body.append(
      this.progressEl,
      el('div', { class: 'hud-progress' }, this.progressBar)
    );

    const lives = this.statShell('LIVES');
    this.livesEl = el('div', {
      class: 'hud-lives',
      attrs: { role: 'img', 'aria-label': `Lives: ${MAX_LIVES} of ${MAX_LIVES}` },
    });
    this.pips = [];
    for (let i = 0; i < MAX_LIVES; i++) {
      const pip = el('span', { class: 'hud-pip is-alive' });
      this.pips.push(pip);
      this.livesEl.append(pip);
    }
    lives.body.append(this.livesEl);

    const time = this.statShell('TIME', ' hud-stat--time');
    this.timeStat = time.node;
    this.timeEl = el('span', { class: 'hud-stat__value', text: formatTime(180) });
    time.body.append(this.timeEl);

    this.topbar = el(
      'div',
      { class: 'hud-topbar', attrs: { role: 'group', 'aria-label': 'Run status' } },
      score.node,
      progress.node,
      lives.node,
      time.node
    );

    // --- Holding slot + objective banner ----------------------------------
    this.holdingIcon = el('div', { class: 'hud-holding__chip', text: '–' });
    this.holdingName = el('span', { class: 'hud-holding__name', text: 'Empty' });
    this.holdingPanel = el(
      'div',
      { class: 'hud-holding', attrs: { role: 'group', 'aria-label': 'Currently holding' } },
      el('span', { class: 'hud-holding__label', text: 'HOLDING' }),
      el('div', { class: 'hud-holding__row' }, this.holdingIcon, this.holdingName)
    );

    this.objectiveTitle = el('div', { class: 'hud-objective__title' });
    this.objectiveDetail = el('div', { class: 'hud-objective__detail' });
    this.objectiveEl = el(
      'div',
      { class: 'hud-objective', attrs: { hidden: true } },
      this.objectiveTitle,
      this.objectiveDetail
    );

    const leftCol = el('div', { class: 'hud-top__left' }, this.holdingPanel, this.objectiveEl);

    // --- Pause / exit ------------------------------------------------------
    const pause = el(
      'button',
      {
        class: 'hud-pause btn btn--primary',
        attrs: { type: 'button', 'aria-label': 'Pause game' },
        on: { click: () => this.opts.onPause() },
      },
      svgRoot(
        svg('rect', { x: 7.4, y: 5.4, width: 3.6, height: 13.2, rx: 1.4, fill: 'currentColor' }),
        svg('rect', { x: 13.0, y: 5.4, width: 3.6, height: 13.2, rx: 1.4, fill: 'currentColor' })
      )
    );

    this.exitBtn = el(
      'button',
      {
        class: 'hud-pause hud-exit btn',
        attrs: { type: 'button', 'aria-label': 'Leave to hub', hidden: true },
        on: { click: () => this.opts.onExit() },
      },
      glyph('back')
    );

    const topRight = el('div', { class: 'hud-top__right' }, this.exitBtn, pause);
    const top = el('div', { class: 'hud-top' }, leftCol, this.topbar, topRight);

    // --- Callouts ---------------------------------------------------------
    this.promptKey = el('span', { class: 'hud-prompt__key', text: 'A' });
    this.promptText = el('span', { class: 'hud-prompt__text' });
    this.promptEl = el(
      'div',
      { class: 'hud-prompt', attrs: { hidden: true } },
      this.promptKey,
      this.promptText
    );

    this.flashIcon = el('div', { class: 'hud-flash__icon' });
    this.flashTitle = el('div', { class: 'hud-flash__title' });
    this.flashDetail = el('div', { class: 'hud-flash__detail' });
    this.flashEl = el(
      'div',
      { class: 'hud-flash', attrs: { hidden: true } },
      this.flashIcon,
      el('div', { class: 'hud-flash__body' }, this.flashTitle, this.flashDetail)
    );

    // Hard-edged red inset frame that flares on damage. A vignette would be a
    // soft ghost shadow, which the design system explicitly forbids.
    this.hitFrame = el('div', { class: 'hud-hit' });

    // --- Controls ---------------------------------------------------------
    const stick = this.buildJoystick();
    this.buttons = {
      a: this.buildActionButton('a', 'hand', 'Interact or collect'),
      b: this.buildActionButton('b', 'b', 'Back or cancel'),
    };
    const actions = el('div', { class: 'hud-actions' }, this.buttons.a, this.buttons.b);
    this.controlsEl = el('div', { class: 'hud-controls' }, stick, actions);

    // --- Accessibility ----------------------------------------------------
    this.live = el('div', {
      class: 'sr-only',
      attrs: { 'aria-live': 'polite', 'aria-atomic': 'true' },
    });
    // Keyboard equivalents, stated once, for players who will never see the
    // touch controls. The joystick itself is hidden from assistive tech because
    // it cannot be dragged with a keyboard.
    const help = el('p', {
      class: 'sr-only',
      text:
        'Move with the arrow keys or W A S D. Press Space or E to interact, ' +
        'X to go back, and Escape to pause.',
    });

    this.wrap.append(top, this.promptEl, this.flashEl, this.hitFrame, this.controlsEl, help, this.live);
    this.root.append(this.wrap);

    this.setVisible(false);
  }

  private buildJoystick(): HTMLElement {
    this.stickKnob = el('div', { class: 'hud-stick__knob' });
    const base = el(
      'div',
      {
        class: 'hud-stick',
        attrs: {
          role: 'application',
          'aria-label': 'Movement joystick',
          // Keyboard users already have full arrow-key movement; exposing an
          // undraggable "application" widget to a screen reader is noise.
          'aria-hidden': 'true',
        },
      },
      el('div', { class: 'hud-stick__ring' }),
      this.stickKnob
    );
    this.stickBase = base;

    let pointerId: number | null = null;
    let radius = 40;

    const setKnob = (x: number, y: number) => {
      this.stickKnob.style.setProperty('--kx', `${x.toFixed(2)}px`);
      this.stickKnob.style.setProperty('--ky', `${y.toFixed(2)}px`);
    };

    const move = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      const r = base.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      const dist = Math.min(Math.hypot(dx, dy), radius);
      const ang = Math.atan2(dy, dx);
      const kx = Math.cos(ang) * dist;
      const ky = Math.sin(ang) * dist;
      setKnob(kx, ky);
      // Screen Y grows downward; gameplay "forward" is up, hence the negation.
      this.input.setStick(kx / radius, -ky / radius);
    };

    const end = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      pointerId = null;
      base.classList.remove('is-active');
      // The knob springs home with a slight overshoot. `is-returning` only adds
      // the transition, so dragging itself stays perfectly 1:1 with the thumb.
      base.classList.add('is-returning');
      setKnob(0, 0);
      window.clearTimeout(this.stickReturnTimer);
      this.stickReturnTimer = window.setTimeout(() => base.classList.remove('is-returning'), 420);
      this.input.releaseStick();
    };

    base.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      pointerId = e.pointerId;
      base.setPointerCapture(e.pointerId);
      base.classList.remove('is-returning');
      base.classList.add('is-active');
      // Travel is derived from the rendered sizes, so the CSS can rescale the
      // control for touch or small viewports without desyncing the input range.
      // At full deflection the knob kisses the rim rather than sailing past it.
      const knobW = this.stickKnob.offsetWidth || 68;
      radius = Math.max(24, base.clientWidth / 2 - knobW * 0.46);
      move(e);
    });
    base.addEventListener('pointermove', move);
    base.addEventListener('pointerup', end);
    base.addEventListener('pointercancel', end);
    base.addEventListener('lostpointercapture', end);

    return base;
  }

  private buildActionButton(which: ActionKey, kind: GlyphKind, label: string): HTMLButtonElement {
    const slot = el('span', { class: 'hud-action__glyph' }, glyph(kind));
    const btn = el(
      'button',
      {
        class: `hud-action hud-action--${which}`,
        attrs: { type: 'button', 'aria-label': label },
      },
      slot,
      el('span', { class: 'hud-action__badge', text: which.toUpperCase(), attrs: { 'aria-hidden': 'true' } })
    );
    this.glyphSlots[which] = slot;

    const down = (e: PointerEvent) => {
      if (btn.disabled) return;
      e.preventDefault();
      btn.setPointerCapture(e.pointerId);
      this.input.pressButton(which);
      btn.classList.add('is-down');
    };
    const up = () => {
      this.input.releaseButton(which);
      btn.classList.remove('is-down');
    };
    btn.addEventListener('pointerdown', down);
    btn.addEventListener('pointerup', up);
    btn.addEventListener('pointercancel', up);
    btn.addEventListener('lostpointercapture', up);

    // Keyboard: Space/Enter already reach `Input` through the global key map, so
    // these handlers only mirror the pressed *look*. Dispatching here as well
    // would double-fire the action edge for anyone tabbing to the button.
    btn.addEventListener('keydown', (e) => {
      if (e.code === 'Space' || e.code === 'Enter') btn.classList.add('is-down');
    });
    btn.addEventListener('keyup', () => btn.classList.remove('is-down'));
    btn.addEventListener('blur', () => btn.classList.remove('is-down'));

    return btn;
  }

  /* ---------------------------------------------------------------- *
   * Session rendering
   * ---------------------------------------------------------------- */

  /** Bind to a session so the HUD updates itself. */
  bind(subscribe: (fn: (s: SessionSnapshot) => void) => () => void): void {
    this.disposers.push(subscribe((s) => this.render(s)));
  }

  render(s: SessionSnapshot): void {
    this.setScore(s.score);
    this.setProgress(s.collected, s.total);
    this.setLives(s.lives);
    this.setClock(s.timeLeftSec, s.phase);

    if (s.phase !== this.shownPhase) {
      this.shownPhase = s.phase;
      // A stale "Press A to interact" hanging over a results screen is a bug the
      // player can see, so ownership of the prompt ends with the playing phase.
      if (s.phase !== 'playing') this.clearPrompt();
      this.wrap.classList.toggle('hud--paused', s.phase === 'paused');
    }
  }

  private setScore(target: number): void {
    if (target === this.targetScore) return;
    const gained = target > this.targetScore;
    this.targetScore = target;

    if (prefersReducedMotion()) {
      this.shownScore = target;
      setText(this.scoreEl, String(target));
      if (gained) restartAnimation(this.scoreEl, 'is-gain');
      return;
    }

    this.tweenFrom = this.shownScore;
    this.tweenStart = performance.now();
    if (gained) restartAnimation(this.scoreEl, 'is-gain');
    if (!this.scoreRaf) this.scoreRaf = requestAnimationFrame(this.stepScore);
  }

  /** easeOutCubic — fast off the mark, gentle landing. Reads as "tallying up". */
  private readonly stepScore = (now: number): void => {
    // Clamp at BOTH ends. A rAF callback is stamped with the time the frame
    // began, which can predate the `performance.now()` captured later in that
    // same frame — leaving `t` slightly negative, and easeOutCubic then
    // overshoots backwards and flashes a negative score on screen.
    const t = Math.min(1, Math.max(0, (now - this.tweenStart) / SCORE_TWEEN_MS));
    const eased = 1 - Math.pow(1 - t, 3);
    this.shownScore = Math.round(this.tweenFrom + (this.targetScore - this.tweenFrom) * eased);
    setText(this.scoreEl, String(this.shownScore));
    if (t < 1) {
      this.scoreRaf = requestAnimationFrame(this.stepScore);
    } else {
      this.scoreRaf = 0;
      this.shownScore = this.targetScore;
      setText(this.scoreEl, String(this.targetScore));
    }
  };

  private setProgress(collected: number, total: number): void {
    const label = `${collected}/${total}`;
    if (label !== this.shownProgress) {
      this.shownProgress = label;
      setText(this.progressEl, label);
      if (collected > 0) restartAnimation(this.progressEl, 'is-gain');
    }
    // Round to a tenth of a percent: sub-pixel width churn would dirty layout
    // every frame for a change nobody can see.
    const pct = total > 0 ? Math.round((collected / total) * 1000) / 10 : 0;
    if (pct !== this.shownPct) {
      this.shownPct = pct;
      this.progressBar.style.width = `${pct}%`;
    }
  }

  private setLives(lives: number): void {
    const n = Math.max(0, Math.min(MAX_LIVES, lives));
    if (n === this.shownLives) return;
    const lost = n < this.shownLives;

    for (let i = 0; i < this.pips.length; i++) {
      const pip = this.pips[i]!;
      const alive = i < n;
      const wasAlive = pip.classList.contains('is-alive');
      pip.classList.toggle('is-alive', alive);
      if (wasAlive && !alive) restartAnimation(pip, 'is-losing');
    }

    this.livesEl.setAttribute('aria-label', `Lives: ${n} of ${MAX_LIVES}`);
    if (lost) {
      restartAnimation(this.livesEl, 'is-hit');
      this.hit();
    }
    this.shownLives = n;
  }

  private setClock(timeLeftSec: number, phase: SessionSnapshot['phase']): void {
    const txt = formatTime(timeLeftSec);
    if (txt !== this.shownTime) {
      this.shownTime = txt;
      setText(this.timeEl, txt);
    }

    const live = phase === 'playing';
    const urgent = live && timeLeftSec <= 30 && timeLeftSec > 0;
    const critical = live && timeLeftSec <= 10 && timeLeftSec > 0;

    if (this.timeStat.classList.contains('is-urgent') !== urgent) {
      this.timeStat.classList.toggle('is-urgent', urgent);
      // Kept for compatibility with the original skeleton contract, which put
      // `is-urgent` on the value node.
      this.timeEl.classList.toggle('is-urgent', urgent);
    }
    if (this.timeStat.classList.contains('is-critical') !== critical) {
      this.timeStat.classList.toggle('is-critical', critical);
    }

    if (timeLeftSec > 31) {
      this.warned30 = false;
      this.warned10 = false;
    }
    if (urgent && !this.warned30) {
      this.warned30 = true;
      this.announce('Thirty seconds remaining.');
    }
    if (critical && !this.warned10) {
      this.warned10 = true;
      this.announce('Ten seconds remaining.');
    }
  }

  /* ---------------------------------------------------------------- *
   * Holding slot
   * ---------------------------------------------------------------- */

  /**
   * Fill the HOLDING panel.
   *
   * @param icon  Short glyph or 1–2 characters shown in the chip (e.g. an emoji
   *              stand-in, a plastic code number, or "PET").
   * @param name  Human-readable item name shown beside the chip.
   * @param tint  Optional CSS colour for the chip background — pass the plastic's
   *              tint so the carried sample reads at a glance.
   */
  setHolding(icon: string, name: string, tint?: string): void {
    this.applyHolding(icon, name, true, tint);
  }

  clearHolding(): void {
    this.applyHolding('–', 'Empty', false);
  }

  private applyHolding(icon: string, name: string, filled: boolean, tint?: string): void {
    const changed = this.holdingIcon.textContent !== icon || this.holdingName.textContent !== name;
    setText(this.holdingIcon, icon);
    setText(this.holdingName, name);
    this.holdingPanel.classList.toggle('is-filled', filled);
    if (tint) this.holdingIcon.style.setProperty('--chip-tint', tint);
    else this.holdingIcon.style.removeProperty('--chip-tint');
    if (changed && filled) restartAnimation(this.holdingIcon, 'is-in');
  }

  /* ---------------------------------------------------------------- *
   * Callouts — prompt, flash, objective
   * ---------------------------------------------------------------- */

  /**
   * Show the contextual interaction callout above the controls, e.g.
   * `hud.prompt('Press A to inspect the newspaper')`.
   *
   * Safe to call every frame: re-issuing the identical prompt is a no-op, so the
   * entry animation never re-triggers while the player stands next to an object.
   *
   * @param text Full sentence shown to the player.
   * @param key  Which button chip to show. Defaults to A; pass `null` for a
   *             passive status such as “Watch”, where no input is expected.
   */
  prompt(text: string, key: 'A' | 'B' | null = 'A'): void {
    const cacheKey = `${key ?? 'none'}|${text}`;
    if (cacheKey === this.promptCache && !this.promptEl.hidden) return;
    this.promptCache = cacheKey;
    setText(this.promptText, text);
    this.promptKey.hidden = key === null;
    if (key !== null) setText(this.promptKey, key);
    this.promptEl.hidden = false;
    restartAnimation(this.promptEl, 'is-in');
  }

  /** Hide the interaction callout. Idempotent. */
  clearPrompt(): void {
    if (this.promptEl.hidden) return;
    this.promptCache = '';
    this.promptEl.hidden = true;
    this.promptEl.classList.remove('is-in');
  }

  /**
   * Show a transient feedback card and announce it to assistive tech.
   *
   * The design system requires feedback that *teaches*: pass the explanation as
   * `detail` rather than shipping a bare "Wrong". Wrong answers stay on screen
   * noticeably longer than correct ones for exactly that reason, and also flare
   * the damage frame so the mistake is felt, not just read.
   *
   * @param kind     'correct' | 'wrong' | 'info' — sets icon, accent and timing.
   * @param title    Short verdict, e.g. "Not recyclable".
   * @param detail   Optional teaching sentence.
   * @param durationMs Override the auto-dismiss delay.
   */
  flash(kind: FlashKind, title: string, detail?: string, durationMs?: number): void {
    this.flashEl.classList.remove('hud-flash--correct', 'hud-flash--wrong', 'hud-flash--info');
    this.flashEl.classList.add(`hud-flash--${kind}`);
    clear(this.flashIcon);
    this.flashIcon.append(flashGlyph(kind));
    setText(this.flashTitle, title);
    setText(this.flashDetail, detail ?? '');
    this.flashDetail.hidden = !detail;
    this.flashEl.hidden = false;
    restartAnimation(this.flashEl, 'is-in');
    this.announce(detail ? `${title}. ${detail}` : title);
    if (kind === 'wrong') this.hit();

    window.clearTimeout(this.flashTimer);
    this.flashTimer = window.setTimeout(() => this.clearFlash(), durationMs ?? FLASH_MS[kind]);
  }

  /** Dismiss the feedback card early. Idempotent. */
  clearFlash(): void {
    window.clearTimeout(this.flashTimer);
    this.flashTimer = 0;
    if (this.flashEl.hidden) return;
    this.flashEl.hidden = true;
    this.flashEl.classList.remove('is-in');
  }

  /**
   * Persistent zone/objective banner under the HOLDING panel — the reference's
   * "Bottle Pile / Pick one unknown sample." card.
   */
  setObjective(title: string, detail?: string): void {
    const changed = this.objectiveTitle.textContent !== title;
    setText(this.objectiveTitle, title);
    setText(this.objectiveDetail, detail ?? '');
    this.objectiveDetail.hidden = !detail;
    this.objectiveEl.hidden = false;
    if (changed) restartAnimation(this.objectiveEl, 'is-in');
  }

  /** Hide the objective banner. */
  clearObjective(): void {
    this.objectiveEl.hidden = true;
  }

  /**
   * Flare the damage frame and shake the status bar.
   *
   * Called automatically when a life is lost or a `flash('wrong', …)` fires;
   * exposed so a scene can also punish, say, walking into a wall.
   */
  hit(): void {
    restartAnimation(this.hitFrame, 'is-on');
    restartAnimation(this.topbar, 'is-hit');
  }

  /* ---------------------------------------------------------------- *
   * Controls
   * ---------------------------------------------------------------- */

  /**
   * Swap an action button's icon as the player's verb changes — for example
   * `setActionGlyph('a', 'scan')` while standing at an identification machine.
   */
  setActionGlyph(which: ActionKey, kind: GlyphKind): void {
    const slot = this.glyphSlots[which];
    if (!slot) return;
    // Early-out is load-bearing, not a micro-optimisation. Scenes call this from
    // their per-frame focus resolver, and without the guard every frame cleared
    // the slot and built a fresh SVG subtree — a DOM allocation plus style
    // recalculation 60x a second, in the exact scene the user reports as laggy.
    // Game 2 had to gate its own calls to work around this; no scene should have
    // to defend itself against a setter.
    if (this.glyphKinds[which] === kind) return;
    this.glyphKinds[which] = kind;
    clear(slot);
    slot.append(glyph(kind));
  }

  /** Update an action button's accessible name, e.g. "Insert bottle into machine". */
  setActionLabel(which: ActionKey, label: string): void {
    this.buttons[which].setAttribute('aria-label', label);
  }

  /**
   * Dim and disable an action button when it has nothing to do. The button stays
   * in the layout — controls that appear and vanish force the player to re-find
   * them mid-run.
   */
  setActionEnabled(which: ActionKey, enabled: boolean): void {
    const btn = this.buttons[which];
    btn.disabled = !enabled;
    btn.classList.toggle('is-disabled', !enabled);
    if (!enabled) {
      btn.classList.remove('is-down');
      this.input.releaseButton(which);
    }
  }

  /** Hide the joystick and A/B buttons, e.g. while a briefing overlay owns the screen. */
  setControlsVisible(on: boolean): void {
    this.controlsEl.classList.toggle('is-hidden', !on);
    if (!on) {
      this.input.releaseStick();
      this.stickBase.classList.remove('is-active', 'is-returning');
      this.stickKnob.style.setProperty('--kx', '0px');
      this.stickKnob.style.setProperty('--ky', '0px');
    }
  }

  /** Show or hide the "leave to hub" button beside pause. Hidden by default. */
  setExitVisible(on: boolean): void {
    this.exitBtn.hidden = !on;
  }

  /* ---------------------------------------------------------------- *
   * Lifecycle
   * ---------------------------------------------------------------- */

  /** Announce to assistive tech without stealing focus. */
  announce(msg: string): void {
    this.live.textContent = '';
    window.clearTimeout(this.announceTimer);
    // Re-setting on a later tick guarantees SRs re-read identical strings.
    this.announceTimer = window.setTimeout(() => (this.live.textContent = msg), 30);
  }

  setVisible(on: boolean): void {
    this.visible = on;
    this.wrap.style.display = on ? '' : 'none';
    if (!on) {
      this.clearPrompt();
      this.clearFlash();
    }
  }

  /**
   * Return every opt-in surface to its default, then hide.
   *
   * The HUD is a single long-lived instance shared by all scenes, so anything a
   * scene switched on stays on until something switches it off. Without this,
   * leaving a game mid-run leaks its objective banner, exit button, holding slot
   * and disabled A/B buttons onto the hub — and onto whatever scene is entered
   * next. The shell calls this on every swap; a scene then opts back into
   * exactly what it needs in `enter()`.
   */
  resetForScene(): void {
    this.clearPrompt();
    this.clearFlash();
    this.clearObjective();
    this.clearHolding();
    this.setExitVisible(false);
    this.setControlsVisible(true);
    for (const which of Object.keys(this.buttons) as ActionKey[]) {
      this.setActionEnabled(which, true);
    }
    this.wrap.classList.remove('hud--paused');
    // Time warnings are per-run, not per-HUD.
    this.warned30 = false;
    this.warned10 = false;
    this.setVisible(false);
  }

  isVisible(): boolean {
    return this.visible;
  }

  /** Toggle touch-optimised control sizing. */
  setTouchControls(on: boolean): void {
    this.wrap.classList.toggle('hud--touch', on);
  }

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
    if (this.scoreRaf) cancelAnimationFrame(this.scoreRaf);
    this.scoreRaf = 0;
    window.clearTimeout(this.flashTimer);
    window.clearTimeout(this.announceTimer);
    window.clearTimeout(this.stickReturnTimer);
    this.pips = [];
    this.glyphSlots = {};
    // Every listener this class registers lives on a node inside `root`, so
    // dropping the subtree drops them with it.
    clear(this.root);
  }
}
